import Button from "@/components/Button";
import Checkbox from "@/components/Checkbox";
import DateSelect from "@/components/DateSelect";
import EmailPreviewDialog, {
  EMAIL_PREVIEW_MODAL_CLASSNAME,
  EmailPreviewModalActions
} from "@/components/EmailPreviewDialog";
import useModal from "@/hooks/useModal";
import { useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { CHANGE_DUE_DATE_MUTATION, COMMERCE_AUDIT_LOG_QUERY } from "../queries";
import { OrderType } from "../types";
import { showSnackbar } from "@/lib/snackbarUtils";
import { NotePriorityValues } from "@/types";
import type { NotePriority } from "@/types";
import NoteInputFields from "@/features/global/components/NotesTable/NoteInputFields";
import { buildNoteInput } from "@/features/global/components/NotesTable/noteHelpers";
import useAmplitude from "@/hooks/useAmplitude";
import { BILLING_ANALYTICS_EVENTS, getBillingAnalyticsData } from "@/pages/billing/_utils/amplitude";
import { useCommercePayorNotificationEmailPreview } from "@/pages/billing/_hooks/useCommercePayorNotificationEmailPreview";
import { CommercePayorNotificationEmailType } from "@/pages/billing/_queries/emailPreviewQueries";

interface ChangeDueDateFormValues {
  orderDueDate: { startDate: string; endDate: string };
  noteContent: string;
  notePriority: NotePriority;
}

export const ChangeDueDateModal = ({
  onDismiss,
  order,
  refetch
}: {
  onDismiss: () => void;
  order: OrderType;
  refetch: () => void;
}) => {
  const { trackEvent } = useAmplitude();
  const { control, watch, getValues } = useForm<ChangeDueDateFormValues>({
    defaultValues: { noteContent: "", notePriority: NotePriorityValues.NONE }
  });
  const headline = "Change Due Date";
  const [changeDueDateMutation, { loading }] = useMutation(CHANGE_DUE_DATE_MUTATION, {
    refetchQueries: [COMMERCE_AUDIT_LOG_QUERY]
  });
  const [errorContent, setErrorContent] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendNotification, setSendNotification] = useState(false);
  const { close, Modal } = useModal();
  const {
    loadPreview,
    resetPreview,
    preview,
    loading: previewLoading,
    error: previewError
  } = useCommercePayorNotificationEmailPreview(order.id);

  useEffect(() => () => resetPreview(), [resetPreview]);

  const orderDueDateRange = watch("orderDueDate", {
    startDate: order.dueDate,
    endDate: order.dueDate
  });

  async function changeDueDate() {
    const noteContent = getValues("noteContent");
    const notePriority = getValues("notePriority");
    const { data } = await changeDueDateMutation({
      variables: {
        input: {
          orderId: order.id,
          dueDate: orderDueDateRange.startDate,
          sendNotification,
          note: buildNoteInput(noteContent, notePriority, headline)
        }
      }
    });

    if (data.commerceChangeOrderDueDate.success) {
      refetch();
      showSnackbar("Due Date Changed");
      if (sendNotification) {
        showSnackbar("Email Notification Sent");
      }
      close();
      trackEvent(
        BILLING_ANALYTICS_EVENTS.orderDueDateChanged,
        getBillingAnalyticsData({
          source: "orders",
          orderId: order.id,
          action: sendNotification ? "change_due_date_with_notification" : "change_due_date"
        })
      );
    } else {
      setErrorContent(data.commerceChangeOrderDueDate.message);
    }
  }

  const handlePreviewEmail = () => {
    setPreviewOpen(true);
    void loadPreview({
      notificationType: CommercePayorNotificationEmailType.INVOICE_AVAILABLE,
      dueDate: orderDueDateRange.startDate
    });
  };

  return (
    <>
      <Modal
        onClosed={onDismiss}
        actions={
          <EmailPreviewModalActions onPreview={handlePreviewEmail} previewDisabled={loading || !sendNotification}>
            <Button variant="text" onClick={close} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={changeDueDate} disabled={loading || !orderDueDateRange.startDate}>
              Change Due Date
            </Button>
          </EmailPreviewModalActions>
        }
        headline={headline}
        className={`${EMAIL_PREVIEW_MODAL_CLASSNAME} max-h-[90vh]`}
        displayOverflow
      >
        {errorContent ? <div className="text-red-500 font-bold text-center mb-4">{errorContent}</div> : null}
        <p>Are you sure you want to update this order&#39;s due date?</p>

        <div className="mt-6">
          <input className="mt-4 ml-1 absolute"></input>

          <Controller
            name="orderDueDate"
            control={control}
            render={({ field }) => (
              <DateSelect
                label="Order Due Date *"
                useModal={true}
                value={orderDueDateRange}
                onChange={(e) => {
                  field.onChange(e);
                }}
                useSingleDate
                showFooter={false}
              />
            )}
          />

          <div className="mt-6">
            <Checkbox
              name="send-notification"
              label="Send email notification to payor"
              checked={sendNotification}
              onChange={() => {
                setSendNotification(!sendNotification);
              }}
            />
          </div>

          <div className="mt-6">
            <NoteInputFields control={control} contentName="noteContent" priorityName="notePriority" />
          </div>
        </div>
      </Modal>
      <EmailPreviewDialog
        open={previewOpen}
        onClosed={() => setPreviewOpen(false)}
        subject={previewLoading ? undefined : preview?.subject}
        html={previewLoading ? undefined : preview?.html}
        loading={previewLoading}
        errorMessage={previewLoading ? null : (previewError?.message ?? null)}
      />
    </>
  );
};
